import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import GoldenNetwork from './GoldenNetwork';

export default function Hero() {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* 3D Background */}
      <GoldenNetwork />

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black z-[1]" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30 z-[1]" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
        {/* Label */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <span className="inline-block text-xs font-mono tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-2">
            Estudio de Marketing Creativo
          </span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-[0.9] mb-8"
          data-testid="hero-headline"
        >
          <span className="text-white">Marketing Estratégico</span>
          <br />
          <span className="gold-text">&amp; Crecimiento Digital</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="max-w-2xl mx-auto text-base md:text-lg text-zinc-400 leading-relaxed mb-12"
          data-testid="hero-subheadline"
        >
          Diseñamos marcas, sistemas digitales y estrategias de crecimiento que transforman
          la atención en resultados medibles para tu negocio.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
        >
          <motion.button
            onClick={() => scrollToSection('#contact')}
            className="group flex items-center gap-3 bg-white text-black hover:bg-[#D4AF37] px-8 py-4 text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="hero-start-project-btn"
          >
            Iniciar Proyecto
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>

          <motion.button
            onClick={() => scrollToSection('#services')}
            className="group flex items-center gap-3 border border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] px-8 py-4 text-xs font-bold tracking-[0.15em] uppercase transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="hero-explore-services-btn"
          >
            <Play size={14} />
            Ver Servicios
          </motion.button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-6 h-10 border border-white/30 rounded-full flex items-start justify-center p-2"
          >
            <motion.div
              animate={{ height: ['20%', '50%', '20%'] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="w-1 bg-[#D4AF37] rounded-full"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
